// ============================================================================
// File: useAlerts.ts
// Purpose: Alert bell polling hook for Spec 160 — Dashboard Redesign
// Author: Peter Dobler (Dobler Data Solutions)
// Created: 2026-05-31
// Feature: 160 - Dashboard Redesign (US5 — Alert Header Redesign)
// Constitution: v1.15.0 | Principle XXXVII (Code Header Standards)
// ============================================================================
/**
 * Polls GET /api/alerts/recent every 20 seconds for Admin/Manager/Operator.
 * Returns early (no poll, no network calls) for Viewer role.
 *
 * Usage:
 *   const { alerts, unacknowledgedCount, loading, acknowledge } = useAlerts();
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../services/apiClient';
import { useAuth } from '../modules/backup/hooks/useAuth';
import type { AlertSummary, AlertsRecentResponse } from '../../../shared/types/dashboard';
import { roleSatisfies } from '../../../shared/access/roles';

const POLL_INTERVAL_MS = 20_000; // 20 seconds per spec C2 / NFR-002

export interface UseAlertsReturn {
  alerts: AlertSummary[];
  unacknowledgedCount: number;
  loading: boolean;
  lastPolledAt: Date | null;
  acknowledge: (ids: string[] | 'all') => Promise<void>;
}

export function useAlerts(): UseAlertsReturn {
  const { user } = useAuth();
  const role = user?.role ?? '';

  // Viewer role: return empty state immediately, no polling
  const isViewer = role.toLowerCase() === 'viewer';

  const [alerts, setAlerts] = useState<AlertSummary[]>([]);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastPolledAt, setLastPolledAt] = useState<Date | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Derive scope from role — supports both PascalCase ('Admin') and lowercase ('admin')
  const roleLower = role.toLowerCase();
  const scope = roleSatisfies(roleLower, ['Admin', 'Operator']) ? 'fleet' : 'client';

  const fetchAlerts = useCallback(async () => {
    if (isViewer) return;
    try {
      const response = await apiClient.get<AlertsRecentResponse>(
        `/api/alerts/recent?limit=10&scope=${scope}`
      );
      setAlerts(response.data?.alerts ?? []);
      setUnacknowledgedCount(response.data?.unacknowledgedCount ?? 0);
      setLastPolledAt(new Date());
    } catch {
      // Silent — polling failures should not surface to users
    } finally {
      setLoading(false);
    }
  }, [isViewer, scope]);

  useEffect(() => {
    if (isViewer) return;

    // Initial fetch
    setLoading(true);
    fetchAlerts();

    // Start polling
    intervalRef.current = setInterval(fetchAlerts, POLL_INTERVAL_MS);

    // Refresh the count IMMEDIATELY when the tab regains focus/visibility, so an
    // acknowledgement made by another user (another browser/session) is reflected the
    // moment the operator returns to this tab — not up to POLL_INTERVAL_MS later.
    const refreshOnFocus = (): void => {
      if (document.visibilityState === 'visible') void fetchAlerts();
    };
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnFocus);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [isViewer, fetchAlerts]);

  const acknowledge = useCallback(async (ids: string[] | 'all') => {
    if (isViewer) return;
    try {
      const body = ids === 'all'
        ? { acknowledgeAll: true }
        : { alertIds: ids };
      await apiClient.post('/api/alerts/acknowledge', body);
      // Refresh immediately after acknowledge
      await fetchAlerts();
    } catch {
      // Silent
    }
  }, [isViewer, fetchAlerts]);

  return { alerts, unacknowledgedCount, loading, lastPolledAt, acknowledge };
}
